import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Plugin,
} from "chart.js";
import { ChartsLegend } from "./ChartsLegend";

ChartJS.register(ArcElement, Tooltip, Legend);

// Custom plugin to display text in the center of the doughnut chart
const centerTextPlugin: Plugin<"doughnut"> = {
  id: "centerText",
  afterDraw: (chart: any) => {
    const ctx = chart.ctx;
    const centerX = chart.chartArea.left + (chart.chartArea.right - chart.chartArea.left) / 2;
    const centerY = chart.chartArea.top + (chart.chartArea.bottom - chart.chartArea.top) / 2;

    ctx.save();
    ctx.font = "24px Arial";
    ctx.fillStyle = "#607783";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(chart.config.options.plugins?.centerText?.text || "", centerX, centerY);
    ctx.restore();
  },
};

const DashboardCircleChart: React.FC<{
  total: number | string;
  coloredValue: number;
  grayValue: number;
  coloredValueTitle?: string;
  grayValueTitle?: string;
  coloredValueBgColor?: string;
  grayValueBgColor?: string;
  isPercentage?: boolean;
}> = ({
  total,
  coloredValue,
  grayValue,
  coloredValueTitle,
  grayValueTitle,
  coloredValueBgColor = "#44AEB0",
  grayValueBgColor = "#C1C9CD",
  isPercentage = false,
}) => {
  const data = useMemo(
    () => ({
      datasets: [
        {
          data: [
            coloredValue,
            isPercentage ? grayValue - coloredValue : grayValue,
          ],
          backgroundColor: [coloredValueBgColor, grayValueBgColor],
          borderColor: [coloredValueBgColor, grayValueBgColor],
          borderWidth: 0.25,
        },
      ],
    }),
    [coloredValue, grayValue, isPercentage, coloredValueBgColor, grayValueBgColor]
  );

  const options = useMemo(
    () => ({
      plugins: {
        legend: {
          display: false,
          labels: {
            color: "rgb(255, 99, 132)",
          },
        },
        tooltip: {
          callbacks: {
            label: function (tooltipItem: any) {
              const value =
                tooltipItem.parsed === grayValue - coloredValue
                  ? grayValue
                  : tooltipItem.parsed;
              return `${value}`;
            },
          },
        },
        centerText: {
          text: total,
        },
      },
      cutout: "75%",
    }),
    [total, grayValue, coloredValue]
  );

  return (
    <>
      <Box
        sx={{
          width: 120,
          height: 120,
          display: "inline-flex",
        }}
      >
        <Doughnut data={data} options={options} plugins={[centerTextPlugin]} />
      </Box>
      {coloredValueTitle && grayValueTitle && (
        <ChartsLegend
          usedValueTitle={`${coloredValue} ${coloredValueTitle}`}
          capValueTitle={`${grayValue} ${grayValueTitle}`}
          usedValueBgColor={coloredValueBgColor}
          capValueBgColor={grayValueBgColor}
        />
      )}
    </>
  );
};

export default DashboardCircleChart;
